/**
 * ResizeObserver 错误修复工具
 * 用于捕获和静默处理 ResizeObserver 相关的错误
 */
// 存储原始的错误处理函数
const originalConsoleError = console.error;
// 需要忽略的错误消息模式
const IGNORED_ERROR_PATTERNS = [
    /ResizeObserver loop completed with undelivered notifications/,
    /ResizeObserver loop limit exceeded/,
    /Non-Error promise rejection captured/,
    /Script error/
];
/**
 * 检查是否是需要忽略的错误
 */
function shouldIgnoreError(message) {
    return IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(message));
}
/**
 * 自定义的 console.error 处理函数
 */
function customConsoleError(...args) {
    const message = args.join(' ');
    // 如果是需要忽略的错误，则不输出到控制台
    if (shouldIgnoreError(message)) {
        return;
    }
    // 其他错误正常输出
    originalConsoleError.apply(console, args);
}
/**
 * 全局错误处理函数
 */
function handleGlobalError(event) {
    const message = event.message || '';
    // 如果是需要忽略的错误，阻止默认处理
    if (shouldIgnoreError(message)) {
        event.preventDefault();
        return false;
    }
    // 其他错误继续正常处理
    return true;
}
/**
 * 未处理的 Promise 拒绝处理函数
 */
function handleUnhandledRejection(event) {
    const reason = event.reason;
    const message = typeof reason === 'string' ? reason : reason?.message || '';
    // 如果是需要忽略的错误，阻止默认处理
    if (shouldIgnoreError(message)) {
        event.preventDefault();
        return false;
    }
    // 其他错误继续正常处理
    return true;
}
/**
 * 设置 ResizeObserver 错误处理
 */
export function setupResizeObserverErrorHandler() {
    // 替换 console.error
    console.error = customConsoleError;
    // 监听全局错误
    window.addEventListener('error', handleGlobalError);
    // 监听未处理的 Promise 拒绝
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    console.log('🛡️ ResizeObserver 错误处理已初始化');
}
/**
 * 移动端 ResizeObserver 修复
 */
export function setupMobileResizeObserverFix() {
    // 移动端特殊处理
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        // 延迟处理 ResizeObserver 回调
        const originalResizeObserver = window.ResizeObserver;
        if (originalResizeObserver) {
            window.ResizeObserver = class extends originalResizeObserver {
                constructor(callback) {
                    const wrappedCallback = (entries, observer) => {
                        requestAnimationFrame(() => {
                            try {
                                callback(entries, observer);
                            }
                            catch (error) {
                                if (!shouldIgnoreError(String(error))) {
                                    throw error;
                                }
                            }
                        });
                    };
                    super(wrappedCallback);
                }
            };
        }
    }
}
/**
 * Element Plus ResizeObserver 修复
 */
export function fixElementPlusResizeObserver() {
    // 针对 Element Plus 组件的特殊处理
    const observer = new MutationObserver(() => {
        // 查找所有 Element Plus 组件
        const elComponents = document.querySelectorAll('[class*="el-"]');
        elComponents.forEach(el => {
            // 为每个组件添加错误处理
            if (el && typeof el.addEventListener === 'function') {
                el.addEventListener('error', (event) => {
                    if (shouldIgnoreError(event.message)) {
                        event.stopPropagation();
                        event.preventDefault();
                    }
                });
            }
        });
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}
/**
 * 清理错误处理（用于测试或特殊情况）
 */
export function cleanupErrorHandler() {
    // 恢复原始的 console.error
    console.error = originalConsoleError;
    // 移除事件监听器
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    console.log('🔧 ResizeObserver 错误处理已清理');
}
/**
 * 手动忽略特定错误
 */
export function ignoreError(error) {
    const message = typeof error === 'string' ? error : error?.message || '';
    if (shouldIgnoreError(message)) {
        return true;
    }
    return false;
}
// 开发环境下的调试信息
if (process.env.NODE_ENV === 'development') {
    console.log('🔍 ResizeObserver 错误处理工具已加载');
}
